import { Trans } from '@lingui/react/macro';

import config from '@/config';
import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import IconPackage from '@/icons/Package.svg';
import IconPrint from '@/icons/Print.svg';
import IconTracking from '@/icons/Tracking.svg';
import { canBeCreatedBy, ShipmentWithRelations } from '@/models/shipment';
import { ShipmentMessageTitle } from '@/routes/Shipments/Shipment/components/ShipmentStep/components/ShipmentMessageTitle';
import { TrackingIdModal } from '@/routes/Shipments/Shipment/components/TrackingIdModal/TrackingIdModal';
import { useCurrentSession } from '@/services/auth';

const FinalisationMessage = ({
  shipment,
  hideIcon,
  className,
}: {
  shipment: ShipmentWithRelations;
  hideIcon?: boolean;
  className?: string;
}) => {
  const { currentSession } = useCurrentSession();
  const canCreateShipment = canBeCreatedBy(shipment, currentSession);

  const hasCustomsFiles = shipment.articles.some((shipmentArticle) =>
    shipmentArticle.article.media?.some((medium) => medium.type === 'customs-file')
  );

  const listOfDocumentsToGetParam = `?shippingLabel=1&packingList=1${hasCustomsFiles ? '&customsFiles=1' : ''}`;
  const mediaHref = `${config.apiUrl}/shipments/${shipment.id}/media${listOfDocumentsToGetParam}`;

  if (!canCreateShipment) {
    const originName = shipment.originEntity?.name;
    const destinationName = shipment.destinationEntity?.name;

    return (
      <AlertBar
        type="info"
        size="large"
        icon={<IconPackage />}
        hideIcon={hideIcon}
        className={className}
        title={
          <ShipmentMessageTitle shipment={shipment}>
            <Trans id="requests.transit.transit-pending-message.title.finalisation.wait">
              Waiting for package finalisation
            </Trans>
          </ShipmentMessageTitle>
        }
      >
        {!!originName && !!destinationName && (
          <Trans id="requests.transit.transit-pending-message.text-entity-to-entity.finalisation.wait">
            {originName} is finishing the package for delivery to {destinationName}.
          </Trans>
        )}
        {!!originName && !!shipment.destinationClientId && (
          <Trans id="requests.delivery.delivery-pending-message.text-entity-to-client.finalisation.wait">
            {originName} is finishing the package for delivery to the client.
          </Trans>
        )}
      </AlertBar>
    );
  }

  return (
    <AlertBar
      type="warning"
      size="large"
      icon={<IconPackage />}
      hideIcon={hideIcon}
      className={className}
      title={
        <ShipmentMessageTitle shipment={shipment}>
          <Trans id="shipment.finalisation-message.title">Finalize the package</Trans>
        </ShipmentMessageTitle>
      }
    >
      <Stack gap="0.5rem" alignItems="flex-start">
        <p>
          <Trans id="shipment.finalisation-message.text.with-packing-list">
            Please print the packing list and shipping label. Place the packing list inside the
            package and seal it. Then affix the shipping label to the outside of the package.
          </Trans>
          {hasCustomsFiles && (
            <>
              {' '}
              <Trans id="shipment.finalisation-message.text.customs-files">
                Also print and attach the customs document to the outside of the package.
              </Trans>
            </>
          )}
        </p>

        <Stack row gap="8px">
          {shipment.carrier === 'private-carrier' && (
            <TrackingIdModal shipment={shipment}>
              <Button variant="secondary" size="medium">
                <IconTracking />
                {shipment.trackingId ? (
                  <Trans id="shipment.finalisation-message.action.edit-tracking-id">
                    Edit tracking number
                  </Trans>
                ) : (
                  <Trans id="shipment.finalisation-message.action.add-tracking-id">
                    Add tracking number
                  </Trans>
                )}
              </Button>
            </TrackingIdModal>
          )}
          <Button variant="secondary" size="medium" href={mediaHref} download>
            <IconPrint />
            <Trans id="shipment.finalisation-message.action.with-packing-list">
              Print shipping documents
            </Trans>
          </Button>
        </Stack>
      </Stack>
    </AlertBar>
  );
};

export default FinalisationMessage;
